(() => {
  const initAtlas = (atlas) => {
    if (!atlas || atlas.dataset.mapInitialized === 'true' || typeof window.cytoscape !== 'function') return;

    const dataElement = atlas.querySelector('[data-atlas-data]');
    const container = atlas.querySelector('[data-atlas-canvas]');
    if (!dataElement || !container) return;

    let atlasData;
    try {
      atlasData = JSON.parse(dataElement.textContent);
    } catch (error) {
      console.error('Unable to parse deity atlas data.', error);
      return;
    }

    atlas.dataset.mapInitialized = 'true';
    const traditions = (atlasData.traditions || []).sort((a, b) => a.order - b.order);
    const figures = (atlasData.figures || []).sort((a, b) => a.order - b.order);
    const relationships = (atlasData.relationships || []).sort((a, b) => a.order - b.order);
    const historicalEvents = (atlasData.historicalEvents || []).sort((a, b) => a.startYear - b.startYear || a.order - b.order);
    const guidedRoutes = (atlasData.guidedRoutes || []).sort((a, b) => a.order - b.order);
    const guidedRouteSteps = (atlasData.guidedRouteSteps || []).sort((a, b) => a.stepNumber - b.stepNumber);
    const labels = atlasData.labels || {};
    const figuresById = new Map(figures.map((figure) => [figure.id, figure]));
    const traditionsById = new Map(traditions.map((tradition) => [tradition.id, tradition]));
    const relationshipsById = new Map(relationships.map((relationship) => [relationship.id, relationship]));
    const guidedRoutesById = new Map(guidedRoutes.map((route) => [route.id, route]));
    const guidedStepsByRoute = new Map(guidedRoutes.map((route) => [
      route.id,
      guidedRouteSteps.filter((step) => step.routeId === route.id && figuresById.has(step.figureId))
    ]));
    const figuresByTradition = new Map();

    traditions.forEach((tradition) => figuresByTradition.set(tradition.id, []));
    figures.forEach((figure) => {
      if (!figuresByTradition.has(figure.traditionId)) figuresByTradition.set(figure.traditionId, []);
      figuresByTradition.get(figure.traditionId).push(figure);
    });

    const slugify = (value) => String(value || 'other')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
    const relationshipKind = (relationship) => {
      const type = String(relationship.type || '').toLowerCase();
      if (type.includes('parent')) return 'family-parent';
      if (type.includes('kinship aspect')) return 'family-parent';
      if (type.includes('divine pair')) return 'family-pair';
      if (type.includes('sibling')) return 'family-sibling';
      if (type.includes('kinship')) return 'family-sibling';
      return '';
    };
    const figureLabel = (figure, includeTradition = false) => [
      figure.symbol || '✦',
      figure.name,
      includeTradition ? figure.traditionName : ''
    ].filter(Boolean).join('\n');

    const relationshipGroups = ['historical', 'kinship', 'cultic', 'conflict', 'contextual', 'modern'].map((id) => ({
      id,
      label: atlas.querySelector(`[data-atlas-type="${id}"]`)?.textContent.trim() || id
    }));
    const relatedClassNames = relationshipGroups.map((group) => `is-related-${group.id}`).join(' ');
    const comparativeConstellations = [
      {
        id: 'norse-slavic-thunder',
        figures: ['odin', 'thor', 'sif', 'perun', 'veles'],
        relationships: ['odin-thor', 'thor-sif', 'thor-perun', 'perun-veles'],
        desktopPositions: {
          odin: { x: -260, y: -135 },
          thor: { x: -70, y: 0 },
          sif: { x: -260, y: 135 },
          perun: { x: 165, y: 0 },
          veles: { x: 340, y: 135 }
        },
        compactPositions: {
          odin: { x: -90, y: -220 },
          sif: { x: 90, y: -220 },
          thor: { x: 0, y: -70 },
          perun: { x: 0, y: 100 },
          veles: { x: 0, y: 250 }
        }
      }
    ];
    const thematicRoutes = {
      magic: { keywords: ['magic', 'witch', 'sorcery', 'divination'] },
      thunder: { keywords: ['thunder', 'storm', 'lightning'] },
      love: { keywords: ['love', 'fertility', 'beauty', 'sexuality', 'marriage'] },
      underworld: { keywords: ['underworld', 'death', 'dead', 'rebirth', 'afterlife', 'chthonic'] },
      moon: { keywords: ['moon', 'lunar', 'night'] }
    };
    const findCulturalRoute = (figureId) => {
      const eligibleRelationships = relationships.filter((relationship) => ['historical', 'contextual'].includes(relationship.typeGroup));
      const adjacency = new Map();
      eligibleRelationships.forEach((relationship) => {
        if (!adjacency.has(relationship.source)) adjacency.set(relationship.source, []);
        if (!adjacency.has(relationship.target)) adjacency.set(relationship.target, []);
        adjacency.get(relationship.source).push(relationship);
        adjacency.get(relationship.target).push(relationship);
      });
      if (!adjacency.has(figureId)) return null;

      const componentNodes = new Set();
      const queue = [figureId];
      while (queue.length) {
        const nodeId = queue.shift();
        if (componentNodes.has(nodeId)) continue;
        componentNodes.add(nodeId);
        (adjacency.get(nodeId) || []).forEach((relationship) => {
          queue.push(relationship.source === nodeId ? relationship.target : relationship.source);
        });
      }

      if (componentNodes.size < 3 || componentNodes.size > 8) return null;
      const componentRelationships = eligibleRelationships.filter((relationship) => componentNodes.has(relationship.source) && componentNodes.has(relationship.target));
      if (componentRelationships.length !== componentNodes.size - 1) return null;
      if (Array.from(componentNodes).some((nodeId) => (adjacency.get(nodeId) || []).filter((relationship) => componentRelationships.includes(relationship)).length > 2)) return null;

      const endpoints = Array.from(componentNodes).filter((nodeId) => (adjacency.get(nodeId) || []).filter((relationship) => componentRelationships.includes(relationship)).length === 1);
      if (endpoints.length !== 2) return null;
      const firstRelationship = componentRelationships.slice().sort((a, b) => a.order - b.order)[0];
      let currentId = endpoints.includes(firstRelationship.source) ? firstRelationship.source : endpoints[0];
      const orderedNodes = [currentId];
      const orderedRelationships = [];
      const usedRelationships = new Set();

      while (orderedRelationships.length < componentRelationships.length) {
        const nextRelationship = (adjacency.get(currentId) || [])
          .filter((relationship) => componentRelationships.includes(relationship) && !usedRelationships.has(relationship.id))
          .sort((a, b) => a.order - b.order)[0];
        if (!nextRelationship) return null;
        usedRelationships.add(nextRelationship.id);
        orderedRelationships.push(nextRelationship);
        currentId = nextRelationship.source === currentId ? nextRelationship.target : nextRelationship.source;
        orderedNodes.push(currentId);
      }

      return { nodes: orderedNodes, relationships: orderedRelationships };
    };
    const findComparativeConstellation = (figureId) => {
      const constellation = comparativeConstellations.find((item) => item.figures.includes(figureId));
      if (!constellation) return null;
      if (constellation.figures.some((id) => !figuresById.has(id))) return null;
      if (constellation.relationships.some((id) => !relationshipsById.has(id))) return null;
      return {
        ...constellation,
        relationships: constellation.relationships.map((id) => relationshipsById.get(id))
      };
    };

    const elements = [];
    relationshipGroups.forEach((group) => {
      elements.push({
        group: 'nodes',
        data: { id: `focus-ring-${group.id}`, label: group.label, diameter: 360 },
        classes: `focus-ring ${group.id} is-hidden`
      });
    });
    traditions.forEach((tradition) => {
      elements.push({
        group: 'nodes',
        data: { id: `tradition-${tradition.id}`, label: tradition.name, tradition: tradition.id },
        classes: 'tradition'
      });

      const groups = new Map();
      (figuresByTradition.get(tradition.id) || []).forEach((figure) => {
        const groupName = figure.pantheonGroup || 'Other sacred figures';
        if (!groups.has(groupName)) groups.set(groupName, []);
        groups.get(groupName).push(figure);
      });

      groups.forEach((members, groupName) => {
        const groupId = `group-${tradition.id}-${slugify(groupName)}`;
        elements.push({
          group: 'nodes',
          data: { id: groupId, label: groupName, tradition: tradition.id },
          classes: 'pantheon-group'
        });
        elements.push({
          group: 'edges',
          data: { id: `branch-${tradition.id}-${slugify(groupName)}`, source: `tradition-${tradition.id}`, target: groupId, tradition: tradition.id },
          classes: 'branch'
        });

        members.forEach((figure) => {
          const hasPortrait = Boolean(figure.portraitUrl);
          elements.push({
            group: 'nodes',
            data: {
              id: figure.id,
              label: hasPortrait ? figure.name : figureLabel(figure),
              tradition: figure.traditionId,
              group: groupName,
              portraitUrl: figure.portraitUrl || ''
            },
            classes: `figure${hasPortrait ? ' has-portrait' : ''}`
          });
          elements.push({
            group: 'edges',
            data: { id: `branch-${groupId}-${figure.id}`, source: groupId, target: figure.id, tradition: tradition.id },
            classes: 'branch'
          });
        });
      });
    });

    relationships.forEach((relationship) => {
      const uncertainEvidence = /uncertain|reconstruction|debated|variants differ|not an exclusive/i.test(relationship.evidence || '');
      elements.push({
        group: 'edges',
        data: {
          id: relationship.id,
          source: relationship.source,
          target: relationship.target,
          label: relationship.type,
          typeGroup: relationship.typeGroup,
          relationshipKind: relationshipKind(relationship)
        },
        classes: `relationship ${relationship.typeGroup} ${relationshipKind(relationship)}${uncertainEvidence ? ' is-uncertain' : ''}`
      });
    });

    guidedRoutes.forEach((route) => {
      const steps = guidedStepsByRoute.get(route.id) || [];
      steps.slice(0, -1).forEach((step, index) => {
        const nextStep = steps[index + 1];
        elements.push({
          group: 'edges',
          data: {
            id: `guided-${route.id}-${index}`,
            source: step.figureId,
            target: nextStep.figureId,
            label: step.transitionLabel || '',
            routeId: route.id,
            stepIndex: index
          },
          classes: 'guided-connection is-hidden'
        });
      });
    });

    const cy = window.cytoscape({
      container,
      elements,
      layout: { name: 'preset' },
      minZoom: 0.28,
      maxZoom: 2.4,
      wheelSensitivity: 0.18,
      autoungrabify: true,
      boxSelectionEnabled: false,
      selectionType: 'single',
      style: [
        {
          selector: 'node.focus-ring',
          style: {
            'background-opacity': 0,
            'border-color': '#7fa7a0',
            'border-opacity': 0.42,
            'border-style': 'dashed',
            'border-width': 1,
            'color': '#b9b1a5',
            'events': 'no',
            'font-family': 'Georgia, serif',
            'font-size': 10,
            'font-weight': 700,
            'height': 'data(diameter)',
            'label': 'data(label)',
            'shape': 'ellipse',
            'text-background-color': '#080708',
            'text-background-opacity': 0.86,
            'text-background-padding': 4,
            'text-halign': 'left',
            'text-justification': 'center',
            'text-margin-x': -36,
            'text-margin-y': 12,
            'text-max-width': 120,
            'text-transform': 'uppercase',
            'text-valign': 'top',
            'text-wrap': 'wrap',
            'width': 'data(diameter)',
            'z-index': 0
          }
        },
        { selector: 'node.focus-ring.historical', style: { 'border-color': '#c4a55e', 'border-style': 'solid', 'color': '#d7bc77' } },
        { selector: 'node.focus-ring.kinship', style: { 'border-color': '#78a879', 'border-style': 'solid', 'color': '#91c492' } },
        { selector: 'node.focus-ring.cultic', style: { 'border-color': '#5f9fc4', 'border-style': 'dotted', 'color': '#80bce0' } },
        { selector: 'node.focus-ring.conflict', style: { 'border-color': '#c86f66', 'border-style': 'dashed', 'color': '#e18e86' } },
        { selector: 'node.focus-ring.modern', style: { 'border-color': '#a98ac1', 'border-style': 'dotted', 'color': '#c7a8df' } },
        {
          selector: 'node.tradition',
          style: {
            'background-color': '#c4a55e',
            'border-color': '#f0d58c',
            'border-width': 3,
            'color': '#090809',
            'font-family': 'Georgia, serif',
            'font-size': 18,
            'font-weight': 700,
            'height': 92,
            'label': 'data(label)',
            'shape': 'diamond',
            'text-halign': 'center',
            'text-valign': 'center',
            'width': 150
          }
        },
        {
          selector: 'node.pantheon-group',
          style: {
            'background-color': '#171318',
            'border-color': '#806d44',
            'border-width': 2,
            'color': '#d7bc77',
            'font-family': 'Georgia, serif',
            'font-size': 13,
            'font-weight': 700,
            'height': 54,
            'label': 'data(label)',
            'padding': 10,
            'shape': 'round-rectangle',
            'text-halign': 'center',
            'text-valign': 'center',
            'text-wrap': 'wrap',
            'width': 150
          }
        },
        {
          selector: 'node.figure',
          style: {
            'background-color': '#211c22',
            'border-color': '#8f7848',
            'border-width': 2,
            'color': '#f5f0e6',
            'font-family': 'Georgia, serif',
            'font-size': 12,
            'font-weight': 700,
            'height': 88,
            'label': 'data(label)',
            'line-height': 1.25,
            'overlay-opacity': 0,
            'shape': 'ellipse',
            'text-halign': 'center',
            'text-outline-color': '#080708',
            'text-outline-opacity': 0.94,
            'text-outline-width': 2,
            'text-max-width': 76,
            'text-valign': 'center',
            'text-wrap': 'wrap',
            'width': 88
          }
        },
        {
          selector: 'node.figure.has-portrait',
          style: {
            'background-image': 'data(portraitUrl)',
            'background-fit': 'cover',
            'background-clip': 'node',
            'background-opacity': 1,
            'background-image-opacity': 0.82,
            'font-size': 11,
            'text-background-color': '#080708',
            'text-background-opacity': 0.82,
            'text-background-padding': 2,
            'text-margin-y': -7,
            'text-max-width': 78,
            'text-valign': 'bottom'
          }
        },
        {
          selector: 'node.figure:selected, node.figure.is-focused',
          style: {
            'background-color': '#44371f',
            'border-color': '#f1d486',
            'border-width': 4,
            'font-size': 14,
            'height': 112,
            'text-max-width': 96,
            'width': 112,
            'z-index': 999
          }
        },
        {
          selector: 'node.figure.has-portrait:selected, node.figure.has-portrait.is-focused',
          style: {
            'background-image-opacity': 0.9,
            'text-margin-y': -9,
            'text-max-width': 100
          }
        },
        { selector: 'node.figure.is-related-historical', style: { 'border-color': '#c4a55e', 'border-width': 4 } },
        { selector: 'node.figure.is-related-kinship', style: { 'border-color': '#78a879', 'border-width': 4 } },
        { selector: 'node.figure.is-related-cultic', style: { 'border-color': '#5f9fc4', 'border-width': 4 } },
        { selector: 'node.figure.is-related-conflict', style: { 'border-color': '#c86f66', 'border-width': 4 } },
        { selector: 'node.figure.is-related-contextual', style: { 'border-color': '#7fa7a0', 'border-width': 4 } },
        { selector: 'node.figure.is-related-modern', style: { 'border-color': '#a98ac1', 'border-width': 4 } },
        { selector: 'node.figure.is-route', style: { 'height': 102, 'width': 102 } },
        { selector: 'node.figure.is-route.is-focused', style: { 'background-color': '#44371f', 'border-color': '#f1d486', 'height': 116, 'width': 116 } },
        { selector: 'node.figure.is-constellation', style: { 'height': 102, 'width': 102 } },
        { selector: 'node.figure.is-constellation.constellation-norse', style: { 'border-color': '#9eb8c8', 'border-width': 4 } },
        { selector: 'node.figure.is-constellation.constellation-slavic', style: { 'border-color': '#c88b5c', 'border-width': 4 } },
        { selector: 'node.figure.is-constellation.is-focused', style: { 'background-color': '#44371f', 'border-color': '#f1d486', 'height': 116, 'width': 116 } },
        {
          selector: 'edge.branch',
          style: {
            'curve-style': 'taxi',
            'line-color': '#665737',
            'line-opacity': 0.82,
            'target-arrow-color': '#8f7848',
            'target-arrow-shape': 'triangle',
            'taxi-direction': 'downward',
            'taxi-turn': 32,
            'width': 2
          }
        },
        {
          selector: 'edge.relationship',
          style: {
            'color': '#d8cfbf',
            'curve-style': 'bezier',
            'font-size': 10,
            'font-weight': 600,
            'label': '',
            'line-height': 1.15,
            'line-color': '#7fa7a0',
            'line-style': 'dashed',
            'opacity': 0.9,
            'source-distance-from-node': 8,
            'target-arrow-shape': 'none',
            'target-distance-from-node': 8,
            'text-background-color': '#080708',
            'text-background-opacity': 0.9,
            'text-background-padding': 3,
            'text-justification': 'center',
            'text-max-width': 104,
            'text-outline-color': '#080708',
            'text-outline-width': 1,
            'text-rotation': 'none',
            'text-wrap': 'wrap',
            'width': 3
          }
        },
        { selector: 'edge.historical', style: { 'line-color': '#c4a55e', 'line-style': 'solid', 'width': 4 } },
        { selector: 'edge.kinship', style: { 'line-color': '#78a879', 'line-style': 'solid', 'width': 3 } },
        {
          selector: 'edge.family-parent',
          style: {
            'curve-style': 'taxi',
            'line-color': '#78a879',
            'line-style': 'solid',
            'source-distance-from-node': 5,
            'target-arrow-color': '#91c492',
            'target-arrow-shape': 'triangle',
            'target-distance-from-node': 5,
            'taxi-direction': 'downward',
            'taxi-turn': 48,
            'width': 3
          }
        },
        {
          selector: 'edge.family-pair',
          style: {
            'curve-style': 'straight',
            'line-color': '#d7bc77',
            'line-style': 'dashed',
            'target-arrow-shape': 'none',
            'width': 3
          }
        },
        {
          selector: 'edge.family-sibling',
          style: {
            'control-point-distances': 72,
            'control-point-weights': 0.5,
            'curve-style': 'unbundled-bezier',
            'line-color': '#78a879',
            'line-style': 'dotted',
            'target-arrow-shape': 'none',
            'width': 2
          }
        },
        { selector: 'edge.relationship.is-uncertain', style: { 'line-style': 'dotted', 'opacity': 0.72 } },
        {
          selector: 'edge.guided-connection',
          style: {
            'color': '#f5f0e6',
            'curve-style': 'bezier',
            'font-size': 10,
            'font-weight': 700,
            'label': 'data(label)',
            'line-color': '#c4a55e',
            'line-style': 'dashed',
            'target-arrow-color': '#c4a55e',
            'target-arrow-shape': 'triangle',
            'text-background-color': '#080708',
            'text-background-opacity': 0.94,
            'text-background-padding': 4,
            'text-max-width': 116,
            'text-rotation': 'none',
            'text-wrap': 'wrap',
            'width': 4
          }
        },
        { selector: 'edge.cultic', style: { 'line-color': '#5f9fc4', 'line-style': 'dotted', 'width': 4 } },
        { selector: 'edge.conflict', style: { 'line-color': '#c86f66', 'line-style': 'dashed', 'target-arrow-color': '#c86f66', 'target-arrow-shape': 'triangle', 'width': 3 } },
        { selector: 'edge.modern', style: { 'line-color': '#a98ac1', 'line-style': 'dotted', 'width': 3 } },
        { selector: 'edge.relationship.is-compact-label', style: { 'font-size': 9, 'text-background-padding': 2, 'text-max-width': 76 } },
        { selector: '.is-hidden', style: { 'display': 'none' } },
        { selector: 'edge.is-focused', style: { 'opacity': 1, 'width': 5, 'z-index': 999 } },
        { selector: 'edge.is-route', style: { 'curve-style': 'straight', 'font-size': 10, 'opacity': 1, 'target-arrow-shape': 'triangle', 'width': 5 } },
        { selector: 'edge.is-route.historical', style: { 'target-arrow-color': '#c4a55e' } },
        { selector: 'edge.is-route.contextual', style: { 'target-arrow-color': '#7fa7a0' } },
        { selector: 'edge.is-constellation', style: { 'curve-style': 'straight', 'font-size': 10, 'opacity': 1, 'target-arrow-shape': 'triangle', 'width': 4 } },
        { selector: 'edge.is-constellation.kinship', style: { 'target-arrow-color': '#78a879' } },
        { selector: 'edge.is-constellation.contextual', style: { 'target-arrow-color': '#7fa7a0' } }
      ]
    });

    const typeButtons = Array.from(atlas.querySelectorAll('[data-atlas-type]'));
    const traditionButtons = Array.from(atlas.querySelectorAll('[data-atlas-tradition]'));
    const relationshipCards = Array.from(atlas.querySelectorAll('[data-atlas-relationship]'));
    const resetButton = atlas.querySelector('[data-atlas-reset]');
    const showMoreButton = atlas.querySelector('[data-atlas-show-more]');
    const backButton = atlas.querySelector('[data-atlas-back]');
    const results = atlas.querySelector('[data-atlas-results]');
    const figureSelect = atlas.querySelector('[data-atlas-figure-select]');
    const zoomIn = atlas.querySelector('[data-atlas-zoom-in]');
    const zoomOut = atlas.querySelector('[data-atlas-zoom-out]');
    const fitButton = atlas.querySelector('[data-atlas-fit]');
    const details = atlas.querySelector('[data-atlas-details]');
    const detailsEyebrow = atlas.querySelector('[data-atlas-details-eyebrow]');
    const detailsTitle = atlas.querySelector('[data-atlas-details-title]');
    const detailsMeta = atlas.querySelector('[data-atlas-details-meta]');
    const detailsText = atlas.querySelector('[data-atlas-details-text]');
    const detailsBadges = atlas.querySelector('[data-atlas-details-badges]');
    const detailsLinks = atlas.querySelector('[data-atlas-details-links]');
    const detailsConnections = atlas.querySelector('[data-atlas-details-connections]');
    const mapLegend = atlas.querySelector('[data-atlas-map-legend]');
    const mapLegendItems = new Map(
      Array.from(atlas.querySelectorAll('[data-atlas-map-legend-item]')).map((item) => [item.dataset.atlasMapLegendItem, item])
    );
    const modeButtons = Array.from(atlas.querySelectorAll('[data-atlas-mode]'));
    const searchInput = atlas.querySelector('[data-atlas-search]');
    const searchResults = atlas.querySelector('[data-atlas-search-results]');
    const eraSelect = atlas.querySelector('[data-atlas-era]');
    const compareA = atlas.querySelector('[data-atlas-compare-a]');
    const compareB = atlas.querySelector('[data-atlas-compare-b]');
    const compareButton = atlas.querySelector('[data-atlas-compare]');
    const themeButtons = Array.from(atlas.querySelectorAll('[data-atlas-theme]'));
    const guidedRouteButtons = Array.from(atlas.querySelectorAll('[data-atlas-guided-route]'));
    const timeline = atlas.querySelector('[data-atlas-timeline]');
    const timelineAxis = atlas.querySelector('[data-atlas-timeline-axis]');
    const timelineLanes = atlas.querySelector('[data-atlas-timeline-lanes]');
    const timelineEmpty = atlas.querySelector('[data-atlas-timeline-empty]');
    const timelineScroll = atlas.querySelector('[data-atlas-timeline-scroll]');
    const timelineScopeButtons = Array.from(atlas.querySelectorAll('[data-atlas-timeline-scope]'));
    const mobileOpenButton = atlas.querySelector('[data-atlas-mobile-open]');
    const mobileCloseButton = atlas.querySelector('[data-atlas-mobile-close]');
    const mobileSheetToggle = atlas.querySelector('[data-atlas-mobile-sheet-toggle]');
    const mobilePanelHandles = Array.from(atlas.querySelectorAll('[data-atlas-mobile-panel-handle]'));
    const mobileTabs = Array.from(atlas.querySelectorAll('[data-atlas-mobile-tab]'));
    const mobileDock = atlas.querySelector('.deity-atlas__mobile-dock');
    const mobileContextLabel = atlas.querySelector('[data-atlas-mobile-context-label]');
    const mobileContextTitle = atlas.querySelector('[data-atlas-mobile-context-title]');
    const mobileFiltersPanel = atlas.querySelector('.deity-atlas__filters');
    const mobileExplorerPanel = atlas.querySelector('.deity-atlas__explorer');
    const mobileStage = atlas.querySelector('.deity-atlas__map-shell');
    const mobileMedia = window.matchMedia('(max-width: 740px)');
    let activeType = 'all';
    let activeTradition = traditionsById.has('greek') ? 'greek' : traditions[0]?.id;
    let activeFigure = 'all';
    let activeRouteRelationshipIds = null;
    let activeMode = 'pantheon';
    let activeEra = 'all';
    let activeThematicRoute = null;
    let activeGuidedRoute = null;
    let activeGuidedStep = 0;
    let timelineScope = 'tradition';
    let isMobileFullscreen = false;
    let mobilePreviousFocus = null;
    let figureReturnMode = 'pantheon';
    let visibleRelationshipLimit = 4;
    let pendingFitFrame = null;

    /*
     * iOS Safari treats position: fixed as container-relative when any ancestor
     * establishes a transformed/contained rendering context. The Focal theme can
     * add that context above the section, so the viewport UI must live directly
     * under <body>, not merely outside the map shell. Markers restore the original
     * desktop DOM order when the breakpoint changes.
     */
    const mobilePortal = document.createElement('div');
    const mobilePortalId = `${atlas.id}-mobile-portal`;
    document.getElementById(mobilePortalId)?.remove();
    document.querySelector(`style[data-deity-atlas-mobile-portal-styles="${atlas.id}"]`)?.remove();
    mobilePortal.id = mobilePortalId;
    /*
     * Do not use display: contents here. Mobile Safari can then resolve fixed
     * descendants against the section's old containing block after a scroll.
     * A zero-sized body child keeps the sheets viewport-fixed without adding
     * any document height of its own.
     */
    mobilePortal.style.cssText = 'display:block;position:static;width:0;height:0;margin:0;padding:0;overflow:visible;pointer-events:none;';
    mobilePortal.setAttribute('data-deity-atlas-mobile-portal', '');
    ['--atlas-background', '--atlas-surface', '--atlas-text', '--atlas-muted', '--atlas-accent']
      .forEach((property) => mobilePortal.style.setProperty(property, getComputedStyle(atlas).getPropertyValue(property)));
    document.body.appendChild(mobilePortal);

    /* Re-scope this section's generated CSS to the body-level portal. */
    const mobilePortalStyles = document.createElement('style');
    mobilePortalStyles.dataset.deityAtlasMobilePortalStyles = atlas.id;
    mobilePortalStyles.textContent = Array.from(document.querySelectorAll('style'))
      .map((style) => style.textContent || '')
      .filter((css) => css.includes(`#${atlas.id}`))
      .join('\n')
      .split(`#${atlas.id}`)
      .join(`#${mobilePortalId}`);
    document.head.appendChild(mobilePortalStyles);

    const syncMobilePortalState = () => {
      ['is-mobile-fullscreen', 'is-atlas-stage-visible', 'has-mobile-panel'].forEach((className) => {
        mobilePortal.classList.toggle(className, atlas.classList.contains(className));
      });
    };
    syncMobilePortalState();
    new MutationObserver(syncMobilePortalState).observe(atlas, { attributes: true, attributeFilter: ['class'] });

    const mobileViewportLayers = [details, mobileDock, mobileFiltersPanel, mobileExplorerPanel]
      .filter(Boolean)
      .map((element, index) => {
        const marker = document.createComment(`deity-atlas-mobile-layer-${index}`);
        element.parentNode?.insertBefore(marker, element);
        return { element, marker };
      });

    const mountMobileViewportLayers = () => {
      if (mobileMedia.matches) {
        mobileViewportLayers.forEach(({ element }) => {
          if (element.parentNode !== mobilePortal) mobilePortal.appendChild(element);
        });
        atlas.classList.add('has-viewport-mobile-layers');
        syncMobilePortalState();
        return;
      }

      mobileViewportLayers.forEach(({ element, marker }) => {
        if (marker.parentNode) marker.parentNode.insertBefore(element, marker.nextSibling);
      });
      atlas.classList.remove('has-viewport-mobile-layers');
      syncMobilePortalState();
    };

    mountMobileViewportLayers();
    if (typeof mobileMedia.addEventListener === 'function') {
      mobileMedia.addEventListener('change', mountMobileViewportLayers);
    } else if (typeof mobileMedia.addListener === 'function') {
      mobileMedia.addListener(mountMobileViewportLayers);
    }

    const mobileSheetsAvailable = () => mobileMedia.matches || isMobileFullscreen;

    const clearElement = (element) => element?.replaceChildren();
    const normalizedText = (...values) => values.filter(Boolean).join(' ').toLowerCase();
    const splitTerms = (value) => String(value || '')
      .split(/[,;/|]+/)
      .map((item) => item.trim())
      .filter(Boolean);
    const periodEras = (value) => {
      const period = normalizedText(value);
      const eras = new Set();
      if (/bronze|early dynastic|3rd millennium|third millennium|2nd millennium|second millennium/.test(period)) eras.add('early');
      if (/archaic|classical|hellenistic|1st millennium bce|first millennium bce|ancient greek|ancient mesopotam/.test(period)) eras.add('classical');
      if (/roman|imperial|late antiq|republic|1st century|2nd century|3rd century|4th century/.test(period)) eras.add('roman');
      if (/medieval|middle age|5th century|6th century|7th century|8th century|9th century|10th century|11th century|12th century|13th century|14th century|15th century/.test(period)) eras.add('medieval');
      if (/modern|contemporary|19th|20th|21st|wicca|neo.?pagan/.test(period)) eras.add('modern');
      if (!eras.size && /bce|ancient|antiquity/.test(period)) eras.add('classical');
      return eras;
    };
    const matchesEra = (item) => activeEra === 'all' || periodEras([item.period, item.region].filter(Boolean).join(' ')).has(activeEra);
    const relationshipMatchesEra = (relationship) => activeEra === 'all'
      || periodEras(relationship.period).has(activeEra)
      || (matchesEra(figuresById.get(relationship.source) || {}) && matchesEra(figuresById.get(relationship.target) || {}));
    const updateMobileContext = () => {
      const tradition = traditionsById.get(activeTradition);
      const figure = activeFigure !== 'all' ? figuresById.get(activeFigure) : null;
      const displayedMode = activeFigure !== 'all' ? figureReturnMode : activeMode;
      if (mobileContextLabel) mobileContextLabel.textContent = displayedMode === 'family'
        ? (labels.familyTree || 'Family tree')
        : displayedMode === 'timeline'
          ? (labels.timelineTitle || 'Historical timeline')
          : displayedMode === 'guided'
            ? (labels.guidedRoute || 'Guided route')
            : (labels.pantheon || 'Pantheon');
      if (mobileContextTitle) mobileContextTitle.textContent = figure?.name || tradition?.name || labels.mapDefaultTitle || '';
    };
    const updateModeButtons = () => {
      const displayedMode = activeFigure !== 'all' ? figureReturnMode : activeMode;
      modeButtons.forEach((button) => {
        const selected = button.dataset.atlasMode === displayedMode;
        button.classList.toggle('is-active', selected);
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });
      updateMobileContext();
    };
    const updateThemeButtons = () => themeButtons.forEach((button) => {
      const selected = button.dataset.atlasTheme === activeThematicRoute;
      button.classList.toggle('is-active', selected);
      button.setAttribute('aria-pressed', selected ? 'true' : 'false');
    });
    const updateGuidedRouteButtons = () => guidedRouteButtons.forEach((button) => {
      const selected = button.dataset.atlasGuidedRoute === activeGuidedRoute;
      button.classList.toggle('is-active', selected);
      button.setAttribute('aria-pressed', selected ? 'true' : 'false');
    });
    const updateRouteButtons = () => {
      updateThemeButtons();
      updateGuidedRouteButtons();
    };
    const setSurfaceMode = (mode) => {
      const isTimeline = mode === 'timeline';
      atlas.classList.toggle('is-timeline-mode', isTimeline);
      if (timeline) timeline.hidden = !isTimeline;
      if (!isTimeline) window.requestAnimationFrame(() => cy.resize());
    };
    const evidenceTone = (value) => {
      const evidence = normalizedText(value);
      if (/modern/.test(evidence)) return 'modern';
      if (/documented|attested|secure|direct/.test(evidence)) return 'documented';
      if (/probable|possible|disputed|uncertain|variable|context/.test(evidence)) return 'contextual';
      return 'neutral';
    };
    const findShortestPath = (startId, endId) => {
      if (startId === endId) return { nodes: [startId], relationships: [] };
      const queue = [{ id: startId, nodes: [startId], relationships: [] }];
      const visited = new Set([startId]);
      while (queue.length) {
        const current = queue.shift();
        const adjacent = relationships.filter((relationship) => relationship.source === current.id || relationship.target === current.id);
        for (const relationship of adjacent) {
          const nextId = relationship.source === current.id ? relationship.target : relationship.source;
          if (visited.has(nextId)) continue;
          const next = {
            id: nextId,
            nodes: [...current.nodes, nextId],
            relationships: [...current.relationships, relationship]
          };
          if (nextId === endId) return next;
          visited.add(nextId);
          queue.push(next);
        }
      }
      return null;
    };
    const sharedDomains = (first, second) => {
      const firstDomains = splitTerms(first.domains).map((item) => item.toLowerCase());
      const secondDomains = splitTerms(second.domains).map((item) => item.toLowerCase());
      return firstDomains.filter((domain) => secondDomains.some((candidate) => candidate.includes(domain) || domain.includes(candidate)));
    };
    const addLink = (target, text, url, external = false) => {
      if (!target || !text || !url) return;
      const link = document.createElement('a');
      link.href = url;
      link.textContent = text;
      if (external) {
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
      }
      target.appendChild(link);
    };

    const setMobileSheetExpanded = (expanded) => {
      if (!details || !mobileSheetToggle) return;
      details.classList.remove('is-mobile-sheet-dismissed');
      details.classList.remove('is-mobile-dragging');
      details.style.removeProperty('--atlas-sheet-drag-y');
      details.classList.toggle('is-mobile-sheet-expanded', expanded);
      mobileSheetToggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      if (!expanded) details.scrollTop = 0;
    };

    const dismissMobileSheet = () => {
      if (!details || !mobileSheetToggle) return;
      details.classList.remove('is-mobile-sheet-expanded', 'is-mobile-dragging');
      details.classList.add('is-mobile-sheet-dismissed');
      details.style.removeProperty('--atlas-sheet-drag-y');
      mobileSheetToggle.setAttribute('aria-expanded', 'false');
      details.scrollTop = 0;
    };

    const resetMobilePanelMotion = (panel) => {
      if (!panel) return;
      panel.classList.remove('is-mobile-dragging');
      panel.style.removeProperty('--atlas-sheet-drag-y');
    };

    const setDetails = ({ eyebrow, title, meta, text, links = [], connections = [], badges = [] }) => {
      if (!details) return;
      details.hidden = false;
      if (detailsEyebrow) detailsEyebrow.textContent = eyebrow || '';
      if (detailsTitle) detailsTitle.textContent = title || '';
      if (detailsMeta) detailsMeta.textContent = meta || '';
      if (detailsText) detailsText.textContent = text || '';
      clearElement(detailsLinks);
      clearElement(detailsConnections);
      clearElement(detailsBadges);
      badges.filter((badge) => badge?.text).forEach((badge) => {
        const element = document.createElement('span');
        element.className = `deity-atlas__evidence deity-atlas__evidence--${badge.tone || 'neutral'}`;
        element.textContent = badge.text;
        detailsBadges?.appendChild(element);
      });
      links.forEach((link) => addLink(detailsLinks, link.text, link.url, link.external));
      connections.forEach((connection) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = connection.label;
        button.addEventListener('click', () => {
          focusFigure(connection.id, true);
          if (mobileSheetsAvailable()) showMobileTab('info', false);
        });
        detailsConnections?.appendChild(button);
      });
      updateMobileContext();
    };

    const matchingRelationships = (figureId = null) => relationships.filter((relationship) => {
      const typeMatch = activeType === 'all' || relationship.typeGroup === activeType;
      const figureMatch = !figureId || relationship.source === figureId || relationship.target === figureId;
      return typeMatch && figureMatch;
    });

    const updateButtons = () => {
      typeButtons.forEach((button) => {
        const selected = button.dataset.atlasType === activeType;
        button.classList.toggle('is-active', selected);
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });
      traditionButtons.forEach((button) => {
        const selected = button.dataset.atlasTradition === activeTradition;
        button.classList.toggle('is-active', selected);
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });
      if (backButton) backButton.hidden = activeFigure === 'all';
      if (resetButton) resetButton.hidden = activeType === 'all' && activeFigure === 'all';
      updateMobileContext();
    };

    const updateCards = () => {
      const visibleRelationships = relationships.filter((relationship) => {
        const typeMatch = activeMode === 'family'
          ? relationship.typeGroup === 'kinship'
          : activeType === 'all' || relationship.typeGroup === activeType;
        const routeMatch = !activeRouteRelationshipIds || activeRouteRelationshipIds.has(relationship.id);
        const traditionMatch = activeRouteRelationshipIds ? true : relationship.traditions.includes(activeTradition);
        const figureMatch = activeRouteRelationshipIds ? true : activeFigure === 'all' || relationship.source === activeFigure || relationship.target === activeFigure;
        return typeMatch && routeMatch && traditionMatch && figureMatch && relationshipMatchesEra(relationship);
      });
      const visibleOrder = new Map(visibleRelationships.map((relationship, index) => [relationship.id, index]));
      relationshipCards.forEach((card) => {
        const index = visibleOrder.get(card.dataset.relationshipId);
        card.hidden = index === undefined || index >= visibleRelationshipLimit;
      });
      if (results) results.textContent = results.dataset.resultsTemplate.replace('[count]', visibleRelationships.length);
      if (showMoreButton) {
        const remaining = Math.max(0, visibleRelationships.length - visibleRelationshipLimit);
        showMoreButton.hidden = remaining === 0;
        showMoreButton.setAttribute('aria-expanded', remaining === 0 ? 'true' : 'false');
      }
      const visibleGroups = new Set(activeFigure === 'all' && activeMode === 'pantheon' ? [] : visibleRelationships.map((relationship) => relationship.typeGroup));
      relationshipGroups.forEach((group) => {
        const item = mapLegendItems.get(group.id);
        if (!item) return;
        item.hidden = !visibleGroups.has(group.id);
        const label = item.querySelector('[data-atlas-map-legend-label]');
        if (label) label.textContent = group.label;
      });
      if (mapLegend) mapLegend.hidden = visibleGroups.size === 0;
    };

    const populateFigurePicker = () => {
      if (!figureSelect) return;
      figureSelect.replaceChildren();
      const allOption = document.createElement('option');
      allOption.value = 'all';
      allOption.textContent = labels.allFigures || 'All sacred figures';
      figureSelect.appendChild(allOption);
      (figuresByTradition.get(activeTradition) || []).forEach((figure) => {
        const option = document.createElement('option');
        option.value = figure.id;
        option.textContent = figure.name;
        figureSelect.appendChild(option);
      });
      figureSelect.value = activeFigure;
    };

    const showPantheonDetails = () => {
      const tradition = traditionsById.get(activeTradition);
      const count = (figuresByTradition.get(activeTradition) || []).filter(matchesEra).length;
      if (!tradition) return;
      const links = tradition.guideUrl
        ? [{ text: labels.readPantheonGuide?.replace('[name]', tradition.name) || `Read the ${tradition.name} pantheon guide`, url: tradition.guideUrl }]
        : [];
      setDetails({
        eyebrow: labels.pantheon || 'Pantheon',
        title: tradition.name,
        meta: [tradition.period, tradition.region, (labels.figuresCount || '[count] figures').replace('[count]', count)].filter(Boolean).join(' · '),
        text: tradition.summary || labels.mapDefaultText,
        links
      });
    };

    const showFigureDetails = (figure) => {
      const connected = matchingRelationships(figure.id).map((relationship) => {
        const relatedId = relationship.source === figure.id ? relationship.target : relationship.source;
        const related = figuresById.get(relatedId);
        return related ? { id: relatedId, label: `${relationship.type}: ${related.name}` } : null;
      }).filter(Boolean);
      const links = [];
      if (figure.guideUrl) links.push({ text: labels.readGuide?.replace('[name]', figure.name), url: figure.guideUrl });
      if (figure.sourceUrl) links.push({ text: figure.sourceLabel || labels.verifiedSource || labels.viewSource, url: figure.sourceUrl, external: true });
      setDetails({
        eyebrow: `${figure.figureType || labels.sacredFigure} · ${figure.pantheonGroup || labels.pantheonGroup}`,
        title: figure.name,
        meta: [figure.traditionName, figure.period, figure.region, figure.domains].filter(Boolean).join(' · '),
        text: figure.summary,
        links,
        badges: figure.sourceUrl ? [{ text: labels.sourceAvailable || labels.verifiedSource }] : [],
        connections: connected
      });
    };

    const showRelationshipDetails = (relationship) => {
      const source = figuresById.get(relationship.source);
      const target = figuresById.get(relationship.target);
      const links = [];
      if (source?.guideUrl) links.push({ text: labels.readGuide?.replace('[name]', source.name), url: source.guideUrl });
      if (target?.guideUrl && target.guideUrl !== source?.guideUrl) links.push({ text: labels.readGuide?.replace('[name]', target.name), url: target.guideUrl });
      if (relationship.sourceUrl) links.push({ text: relationship.sourceLabel || labels.viewSource, url: relationship.sourceUrl, external: true });
      setDetails({
        eyebrow: relationship.type,
        title: relationship.name,
        meta: [relationship.evidence, relationship.period].filter(Boolean).join(' · '),
        text: relationship.summary,
        links,
        badges: [
          { text: relationship.evidence, tone: evidenceTone(relationship.evidence) },
          relationship.sourceUrl ? { text: labels.sourceAvailable || labels.verifiedSource, tone: 'documented' } : null
        ]
      });
    };

    const stopMapMotion = () => {
      if (pendingFitFrame !== null) {
        window.cancelAnimationFrame(pendingFitFrame);
        pendingFitFrame = null;
      }
      cy.stop(true, false);
    };

    const fitVisible = (duration = 350, padding = 65, offsetY = 0, offsetX = 0) => {
      const visible = cy.elements().filter((element) => !element.hasClass('is-hidden'));
      if (!visible.length) return;

      const bounds = visible.boundingBox({ includeLabels: true, includeOverlays: false });
      if (!Number.isFinite(bounds.w) || !Number.isFinite(bounds.h) || bounds.w <= 0 || bounds.h <= 0) return;

      const safePadding = isMobileFullscreen && !mobileMedia.matches
        ? Math.max(12, Math.min(padding, 42))
        : padding;
      let availableLeft = safePadding;
      let availableRight = container.clientWidth - safePadding;
      let availableTop = safePadding;
      let availableBottom = container.clientHeight - safePadding;

      if (isMobileFullscreen && !mobileMedia.matches) {
        const containerRect = container.getBoundingClientRect();
        const relativeRect = (element) => {
          if (!element || element.hidden) return null;
          const style = window.getComputedStyle(element);
          if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) return null;
          const rect = element.getBoundingClientRect();
          if (rect.width <= 0 || rect.height <= 0 || rect.right <= containerRect.left || rect.left >= containerRect.right) return null;
          return {
            left: rect.left - containerRect.left,
            right: rect.right - containerRect.left,
            top: rect.top - containerRect.top,
            bottom: rect.bottom - containerRect.top
          };
        };
        const filtersRect = mobileFiltersPanel?.classList.contains('is-mobile-panel-active')
          ? relativeRect(mobileFiltersPanel)
          : null;
        const explorerRect = mobileExplorerPanel?.classList.contains('is-mobile-panel-active')
          ? relativeRect(mobileExplorerPanel)
          : null;
        const detailsRect = !atlas.classList.contains('has-mobile-panel')
          && !details?.classList.contains('is-mobile-sheet-dismissed')
          ? relativeRect(details)
          : null;
        const toolbarRect = relativeRect(atlas.querySelector('.deity-atlas__map-toolbar'));

        availableLeft = Math.max(106 + safePadding, filtersRect ? filtersRect.right + 18 : 0);
        availableRight = Math.min(
          container.clientWidth - safePadding,
          explorerRect ? explorerRect.left - 18 : container.clientWidth,
          detailsRect ? detailsRect.left - 18 : container.clientWidth
        );
        availableTop = Math.max(safePadding, toolbarRect ? toolbarRect.bottom + 18 : 0);
      }

      const availableWidth = Math.max(120, availableRight - availableLeft);
      const availableHeight = Math.max(120, availableBottom - availableTop);
      const zoom = Math.max(
        cy.minZoom(),
        Math.min(cy.maxZoom(), availableWidth / bounds.w, availableHeight / bounds.h)
      );
      const pan = {
        x: (availableLeft + availableRight) / 2 - ((bounds.x1 + bounds.x2) / 2) * zoom + offsetX,
        y: (availableTop + availableBottom) / 2 - ((bounds.y1 + bounds.y2) / 2) * zoom + offsetY
      };

      cy.stop(true, false);
      if (duration <= 0) cy.viewport({ zoom, pan });
      else cy.animate({ zoom, pan }, { duration });
    };

    const scheduleFit = (duration = 350, padding = 65, offsetY = 0, offsetX = 0) => {
      if (pendingFitFrame !== null) window.cancelAnimationFrame(pendingFitFrame);
      pendingFitFrame = window.requestAnimationFrame(() => {
        pendingFitFrame = null;
        fitVisible(duration, padding, offsetY, offsetX);
      });
    };

    const showMobileTab = (tabId, expandInfo = false) => {
      if (!mobileSheetsAvailable()) return;
      const showInfo = tabId === 'info';
      const showFilters = tabId === 'pantheons';
      const showTools = tabId === 'tools';
      resetMobilePanelMotion(details);
      resetMobilePanelMotion(mobileFiltersPanel);
      resetMobilePanelMotion(mobileExplorerPanel);
      mobileFiltersPanel?.classList.toggle('is-mobile-panel-active', showFilters);
      mobileExplorerPanel?.classList.toggle('is-mobile-panel-active', showTools);
      atlas.classList.toggle('has-mobile-panel', showFilters || showTools);
      syncMobilePortalState();
      mobileTabs.forEach((button) => {
        const selected = button.dataset.atlasMobileTab === tabId;
        button.classList.toggle('is-active', selected);
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });
      if (showInfo) setMobileSheetExpanded(expandInfo);
      else if (showFilters || showTools) setMobileSheetExpanded(false);
      else dismissMobileSheet();
      if (tabId === 'tools') window.setTimeout(() => searchInput?.focus(), 180);
      if (!mobileMedia.matches) scheduleFit(240, 65);
    };

    if ('IntersectionObserver' in window && mobileStage) {
      const mobileStageObserver = new IntersectionObserver(([entry]) => {
        const stageVisible = entry.isIntersecting;
        atlas.classList.toggle('is-atlas-stage-visible', stageVisible);
        syncMobilePortalState();
        if (!stageVisible && mobileMedia.matches && !isMobileFullscreen) showMobileTab('none');
        if (stageVisible && mobileMedia.matches && !isMobileFullscreen) showMobileTab('info', false);
      }, {
        threshold: 0,
        rootMargin: '0px 0px -8% 0px'
      });
      mobileStageObserver.observe(mobileStage);
    } else {
      atlas.classList.add('is-atlas-stage-visible');
    }

    const bindMobileSheetDrag = (sheet, handle, kind) => {
      if (!sheet || !handle) return;
      let drag = null;
      let suppressClick = false;

      const finishDrag = (event, cancelled = false) => {
        if (!drag || event.pointerId !== drag.pointerId) return;
        const distance = drag.distance;
        const startedExpanded = drag.startedExpanded;
        const moved = Math.abs(distance) > 6;
        drag = null;
        resetMobilePanelMotion(sheet);
        try { handle.releasePointerCapture(event.pointerId); } catch (error) { /* Pointer may already be released. */ }

        if (moved) {
          suppressClick = true;
          window.setTimeout(() => { suppressClick = false; }, 0);
        }
        if (cancelled) {
          if (kind === 'info') setMobileSheetExpanded(startedExpanded);
          return;
        }

        if (kind === 'info') {
          if (startedExpanded) {
            setMobileSheetExpanded(distance <= 72);
          } else if (distance < -54) {
            setMobileSheetExpanded(true);
          } else if (distance > 72) {
            showMobileTab('none');
          } else {
            setMobileSheetExpanded(false);
          }
        } else if (distance > 64) {
          showMobileTab('none');
        }
      };

      handle.addEventListener('pointerdown', (event) => {
        if (!mobileSheetsAvailable() || event.button > 0) return;
        drag = {
          pointerId: event.pointerId,
          startY: event.clientY,
          distance: 0,
          startedExpanded: kind === 'info' && details?.classList.contains('is-mobile-sheet-expanded')
        };
        sheet.classList.add('is-mobile-dragging');
        try { handle.setPointerCapture(event.pointerId); } catch (error) { /* Capture is optional. */ }
      });

      handle.addEventListener('pointermove', (event) => {
        if (!drag || event.pointerId !== drag.pointerId) return;
        drag.distance = event.clientY - drag.startY;
        const canMoveUp = kind === 'info' && !drag.startedExpanded;
        const limitedDistance = canMoveUp
          ? Math.max(-Math.round(window.innerHeight * 0.62), drag.distance)
          : Math.max(0, drag.distance);
        sheet.style.setProperty('--atlas-sheet-drag-y', `${limitedDistance}px`);
      });

      handle.addEventListener('pointerup', (event) => finishDrag(event));
      handle.addEventListener('pointercancel', (event) => finishDrag(event, true));
      handle.addEventListener('click', (event) => {
        if (suppressClick) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }
        if (kind === 'info') {
          showMobileTab('info', !details?.classList.contains('is-mobile-sheet-expanded'));
        } else {
          showMobileTab('none');
        }
      });
    };

    const openMobileFullscreen = () => {
      if (isMobileFullscreen) return;
      isMobileFullscreen = true;
      mobilePreviousFocus = document.activeElement;
      atlas.classList.add('is-mobile-fullscreen');
      atlas.setAttribute('role', 'dialog');
      atlas.setAttribute('aria-modal', 'true');
      document.documentElement.classList.add('deity-atlas-mobile-lock');
      showMobileTab('info', false);
      updateMobileContext();
      window.requestAnimationFrame(() => {
        cy.resize();
        scheduleFit(
          0,
          mobileMedia.matches ? 28 : 65,
          mobileMedia.matches ? 10 : 0
        );
        mobileCloseButton?.focus();
      });
    };

    const closeMobileFullscreen = () => {
      if (!isMobileFullscreen) return;
      isMobileFullscreen = false;
      atlas.classList.remove('is-mobile-fullscreen', 'has-mobile-panel');
      atlas.removeAttribute('role');
      atlas.removeAttribute('aria-modal');
      document.documentElement.classList.remove('deity-atlas-mobile-lock');
      mobileFiltersPanel?.classList.remove('is-mobile-panel-active');
      mobileExplorerPanel?.classList.remove('is-mobile-panel-active');
      resetMobilePanelMotion(mobileFiltersPanel);
      resetMobilePanelMotion(mobileExplorerPanel);
      setMobileSheetExpanded(false);
      window.requestAnimationFrame(() => {
        cy.resize();
        scheduleFit(0);
        if (mobileMedia.matches) showMobileTab('info', false);
        if (mobilePreviousFocus instanceof HTMLElement) mobilePreviousFocus.focus();
      });
    };

    const positionPantheonTree = (traditionId, visibleFigureIds = null) => {
      const grouped = new Map();
      (figuresByTradition.get(traditionId) || []).filter((figure) => !visibleFigureIds || visibleFigureIds.has(figure.id)).forEach((figure) => {
        const groupName = figure.pantheonGroup || 'Other sacred figures';
        if (!grouped.has(groupName)) grouped.set(groupName, []);
        grouped.get(groupName).push(figure);
      });

      const branches = Array.from(grouped.entries()).map(([name, members]) => ({
        name,
        members,
        columns: members.length > 6 ? 3 : members.length > 3 ? 2 : 1,
        width: members.length > 6 ? 360 : members.length > 3 ? 250 : 180
      }));
      const branchGap = 34;
      const totalWidth = branches.reduce((sum, branch) => sum + branch.width, 0) + Math.max(0, branches.length - 1) * branchGap;
      let cursorX = -totalWidth / 2;

      cy.getElementById(`tradition-${traditionId}`).position({ x: 0, y: 55 });
      branches.forEach((branch) => {
        const centerX = cursorX + branch.width / 2;
        cy.getElementById(`group-${traditionId}-${slugify(branch.name)}`).position({ x: centerX, y: 205 });
        const horizontalGap = branch.columns === 1 ? 0 : 108;
        const columnOffset = ((branch.columns - 1) * horizontalGap) / 2;
        branch.members.forEach((figure, index) => {
          const column = index % branch.columns;
          const row = Math.floor(index / branch.columns);
          cy.getElementById(figure.id).position({
            x: centerX - columnOffset + column * horizontalGap,
            y: 350 + row * 120
          });
        });
        cursorX += branch.width + branchGap;
      });
    };

    const showPantheon = (traditionId, animate = true) => {
      if (!traditionsById.has(traditionId)) return;
      stopMapMotion();
      setSurfaceMode('map');
      visibleRelationshipLimit = 4;
      activeMode = 'pantheon';
      activeTradition = traditionId;
      activeFigure = 'all';
      activeRouteRelationshipIds = null;
      activeThematicRoute = null;
      activeGuidedRoute = null;
      const visibleFigures = (figuresByTradition.get(traditionId) || []).filter(matchesEra);
      const visibleFigureIds = new Set(visibleFigures.map((figure) => figure.id));
      const visibleGroups = new Set(visibleFigures.map((figure) => figure.pantheonGroup || 'Other sacred figures'));
      cy.$(':selected').unselect();
      cy.batch(() => {
        cy.elements().addClass('is-hidden').removeClass(`is-focused is-route is-constellation is-compact-label constellation-norse constellation-slavic ${relatedClassNames}`);
        cy.getElementById(`tradition-${traditionId}`).removeClass('is-hidden');
        visibleGroups.forEach((groupName) => {
          const groupId = `group-${traditionId}-${slugify(groupName)}`;
          cy.getElementById(groupId).removeClass('is-hidden');
          cy.getElementById(`branch-${traditionId}-${slugify(groupName)}`).removeClass('is-hidden');
        });
        visibleFigures.forEach((figure) => {
          cy.getElementById(figure.id).data('label', figureLabel(figure)).removeClass('is-hidden');
          cy.getElementById(`branch-group-${traditionId}-${slugify(figure.pantheonGroup || 'Other sacred figures')}-${figure.id}`).removeClass('is-hidden');
        });
      });
      positionPantheonTree(traditionId, visibleFigureIds);
      const tree = cy.elements().filter((element) => !element.hasClass('is-hidden'));
      tree.layout({
        name: 'preset',
        animate: false,
        fit: false,
        padding: 70
      }).run();
      populateFigurePicker();
      showPantheonDetails();
      updateButtons();
      updateModeButtons();
      updateRouteButtons();
      updateCards();
      scheduleFit(animate ? 420 : 0);
    };

    const positionFamilyForest = (familyRelationships, visibleIds) => {
      const visibleIdList = Array.from(visibleIds);
      const figureOrder = new Map(figures.map((figure, index) => [figure.id, index]));
      const parentRelationships = familyRelationships.filter((relationship) => relationshipKind(relationship) === 'family-parent');
      const pairRelationships = familyRelationships.filter((relationship) => relationshipKind(relationship) === 'family-pair');
      const connectedIds = new Set(familyRelationships.flatMap((relationship) => [relationship.source, relationship.target]));
      const childIds = new Set(parentRelationships.map((relationship) => relationship.target));
      const parentIds = new Set(parentRelationships.map((relationship) => relationship.source));
      const partnersOfKnownChildren = new Set();
      const levels = new Map();

      visibleIdList.forEach((id) => levels.set(id, null));
      pairRelationships.forEach((relationship) => {
        if (childIds.has(relationship.source) && !childIds.has(relationship.target)) partnersOfKnownChildren.add(relationship.target);
        if (childIds.has(relationship.target) && !childIds.has(relationship.source)) partnersOfKnownChildren.add(relationship.source);
      });
      Array.from(parentIds)
        .filter((id) => !childIds.has(id) && !partnersOfKnownChildren.has(id))
        .forEach((id) => levels.set(id, 0));

      for (let pass = 0; pass < visibleIdList.length; pass += 1) {
        let changed = false;
        parentRelationships.forEach((relationship) => {
          const parentLevel = levels.get(relationship.source);
          if (!Number.isFinite(parentLevel)) return;
          const childLevel = parentLevel + 1;
          if (!Number.isFinite(levels.get(relationship.target)) || levels.get(relationship.target) < childLevel) {
            levels.set(relationship.target, childLevel);
            changed = true;
          }
        });
        pairRelationships.forEach((relationship) => {
          const sourceLevel = levels.get(relationship.source);
          const targetLevel = levels.get(relationship.target);
          if (Number.isFinite(sourceLevel) && !Number.isFinite(targetLevel)) {
            levels.set(relationship.target, sourceLevel);
            changed = true;
          } else if (!Number.isFinite(sourceLevel) && Number.isFinite(targetLevel)) {
            levels.set(relationship.source, targetLevel);
            changed = true;
          }
        });
        if (!changed) break;
      }

      const unresolvedConnected = visibleIdList.filter((id) => connectedIds.has(id) && !Number.isFinite(levels.get(id)));
      unresolvedConnected.forEach((id) => levels.set(id, 0));
      const mappedLevels = Array.from(levels.values()).filter(Number.isFinite);
      const finalMappedLevel = mappedLevels.length ? Math.max(...mappedLevels) : 0;
      visibleIdList.filter((id) => !connectedIds.has(id)).forEach((id) => levels.set(id, finalMappedLevel + 1));

      const rows = new Map();
      visibleIdList.forEach((id) => {
        const level = levels.get(id) || 0;
        if (!rows.has(level)) rows.set(level, []);
        rows.get(level).push(id);
      });

      const orderedLevels = Array.from(rows.keys()).sort((a, b) => a - b);
      orderedLevels.forEach((level, levelIndex) => {
        const row = rows.get(level);
        const previousRow = levelIndex ? rows.get(orderedLevels[levelIndex - 1]) : [];
        const previousPositions = new Map(previousRow.map((id, index) => [id, index]));
        row.sort((a, b) => {
          const parentPosition = (id) => {
            const parents = parentRelationships.filter((relationship) => relationship.target === id);
            const positions = parents.map((relationship) => previousPositions.get(relationship.source)).filter(Number.isFinite);
            return positions.length ? positions.reduce((sum, position) => sum + position, 0) / positions.length : Number.POSITIVE_INFINITY;
          };
          const positionDifference = parentPosition(a) - parentPosition(b);
          if (Number.isFinite(positionDifference) && positionDifference !== 0) return positionDifference;
          return (figureOrder.get(a) || 0) - (figureOrder.get(b) || 0);
        });

        pairRelationships.forEach((relationship) => {
          if (levels.get(relationship.source) !== level || levels.get(relationship.target) !== level) return;
          const sourceIndex = row.indexOf(relationship.source);
          const targetIndex = row.indexOf(relationship.target);
          if (sourceIndex < 0 || targetIndex < 0 || Math.abs(sourceIndex - targetIndex) === 1) return;
          row.splice(targetIndex, 1);
          row.splice(row.indexOf(relationship.source) + 1, 0, relationship.target);
        });
      });

      const horizontalGap = container.clientWidth < 720 ? 118 : 136;
      const verticalGap = container.clientWidth < 720 ? 158 : 178;
      orderedLevels.forEach((level) => {
        const row = rows.get(level);
        row.forEach((id, index) => cy.getElementById(id).position({
          x: (index - (row.length - 1) / 2) * horizontalGap,
          y: level * verticalGap
        }));
      });
    };

    const showFamilyTree = (traditionId = activeTradition, animate = true) => {
      if (!traditionsById.has(traditionId)) return;
      stopMapMotion();
      setSurfaceMode('map');
      visibleRelationshipLimit = 4;
      activeMode = 'family';
      figureReturnMode = 'family';
      activeType = 'kinship';
      activeTradition = traditionId;
      activeFigure = 'all';
      activeThematicRoute = null;
      activeGuidedRoute = null;
      const familyRelationships = relationships.filter((relationship) => relationship.typeGroup === 'kinship'
        && relationship.traditions.includes(traditionId)
        && relationshipMatchesEra(relationship));
      const fallbackFigures = (figuresByTradition.get(traditionId) || []).filter(matchesEra);
      const visibleIds = new Set(fallbackFigures.map((figure) => figure.id));
      activeRouteRelationshipIds = new Set(familyRelationships.map((relationship) => relationship.id));
      cy.$(':selected').unselect();
      cy.batch(() => {
        cy.elements().addClass('is-hidden').removeClass(`is-focused is-route is-constellation is-compact-label constellation-norse constellation-slavic ${relatedClassNames}`);
        visibleIds.forEach((id) => {
          const figure = figuresById.get(id);
          if (figure) cy.getElementById(id).data('label', figureLabel(figure, true)).removeClass('is-hidden').addClass('is-related-kinship');
        });
        familyRelationships.forEach((relationship) => cy.getElementById(relationship.id).removeClass('is-hidden').addClass('is-focused'));
      });
      const visible = cy.elements().filter((element) => !element.hasClass('is-hidden'));
      if (familyRelationships.length) positionFamilyForest(familyRelationships, visibleIds);
      visible.layout({
        name: familyRelationships.length ? 'preset' : 'grid',
        animate: false,
        fit: false,
        padding: 70
      }).run();
      const tradition = traditionsById.get(traditionId);
      const links = tradition?.guideUrl
        ? [{ text: labels.readPantheonGuide?.replace('[name]', tradition.name) || `Read the ${tradition.name} pantheon guide`, url: tradition.guideUrl }]
        : [];
      setDetails({
        eyebrow: labels.familyTree || 'Family tree',
        title: tradition.name,
        meta: (labels.familyRelationships || '[count] mapped family relationships').replace('[count]', familyRelationships.length),
        text: familyRelationships.length ? tradition.summary : (labels.familyTreeEmpty || ''),
        links
      });
      populateFigurePicker();
      updateButtons();
      updateModeButtons();
      updateRouteButtons();
      updateCards();
      scheduleFit(animate ? 420 : 0, 78);
    };

    const renderSearchResults = (query) => {
      if (!searchResults || !searchInput) return;
      clearElement(searchResults);
      const normalizedQuery = normalizedText(query).trim();
      if (!normalizedQuery) {
        searchResults.hidden = true;
        searchInput.setAttribute('aria-expanded', 'false');
        return;
      }
      const matches = figures.filter((figure) => normalizedText(
        figure.name,
        figure.alternateNames,
        figure.domains,
        figure.symbol,
        figure.traditionName,
        figure.figureType
      ).includes(normalizedQuery)).slice(0, 10);
      if (!matches.length) {
        const empty = document.createElement('p');
        empty.className = 'deity-atlas__search-empty';
        empty.textContent = labels.searchNoResults || 'No matching figures';
        searchResults.appendChild(empty);
      }
      matches.forEach((figure) => {
        const button = document.createElement('button');
        button.type = 'button';
        const symbol = document.createElement('span');
        symbol.setAttribute('aria-hidden', 'true');
        symbol.textContent = figure.symbol || '✦';
        const name = document.createElement('strong');
        name.textContent = figure.name;
        const context = document.createElement('small');
        context.textContent = [figure.traditionName, figure.domains].filter(Boolean).join(' · ');
        button.append(symbol, name, context);
        button.addEventListener('click', () => {
          activeMode = 'pantheon';
          activeType = 'all';
          activeEra = 'all';
          if (eraSelect) eraSelect.value = 'all';
          searchInput.value = figure.name;
          searchResults.hidden = true;
          searchInput.setAttribute('aria-expanded', 'false');
          focusFigure(figure.id, true);
          if (mobileSheetsAvailable()) showMobileTab('info', false);
        });
        searchResults.appendChild(button);
      });
      searchResults.hidden = false;
      searchInput.setAttribute('aria-expanded', 'true');
    };

    const showComparison = (firstId, secondId, animate = true) => {
      const first = figuresById.get(firstId);
      const second = figuresById.get(secondId);
      if (!first || !second || first.id === second.id) return;
      stopMapMotion();
      setSurfaceMode('map');
      visibleRelationshipLimit = 4;
      activeMode = 'compare';
      activeType = 'all';
      activeFigure = 'all';
      activeThematicRoute = null;
      activeGuidedRoute = null;
      activeEra = 'all';
      if (eraSelect) eraSelect.value = 'all';
      const path = findShortestPath(first.id, second.id);
      const nodeIds = path ? path.nodes : [first.id, second.id];
      const pathRelationships = path ? path.relationships : [];
      activeRouteRelationshipIds = new Set(pathRelationships.map((relationship) => relationship.id));
      cy.$(':selected').unselect();
      cy.batch(() => {
        cy.elements().addClass('is-hidden').removeClass(`is-focused is-route is-constellation is-compact-label constellation-norse constellation-slavic ${relatedClassNames}`);
        nodeIds.forEach((id, index) => {
          const figure = figuresById.get(id);
          cy.getElementById(id)
            .data('label', figureLabel(figure, true))
            .removeClass('is-hidden')
            .toggleClass('is-focused', index === 0 || index === nodeIds.length - 1)
            .position({ x: (index - (nodeIds.length - 1) / 2) * 210, y: 0 });
        });
        pathRelationships.forEach((relationship) => cy.getElementById(relationship.id).removeClass('is-hidden').addClass('is-route'));
      });
      const common = sharedDomains(first, second);
      setDetails({
        eyebrow: labels.compareTitle || 'Compare deities',
        title: `${first.name} ↔ ${second.name}`,
        meta: [first.traditionName, second.traditionName].join(' · '),
        text: path
          ? (labels.compareConnected || '[count] documented connections').replace('[count]', pathRelationships.length)
          : (labels.compareNotConnected || 'No documented connection is currently mapped.'),
        badges: common.length ? [{ text: `${labels.sharedDomains || 'Shared domains'}: ${common.join(', ')}` }] : []
      });
      [first, second].forEach((figure) => {
        const card = document.createElement('article');
        card.className = 'deity-atlas__compare-card';
        const title = document.createElement('strong');
        title.textContent = `${figure.symbol || '✦'} ${figure.name}`;
        const meta = document.createElement('small');
        meta.textContent = [figure.traditionName, figure.period].filter(Boolean).join(' · ');
        const domains = document.createElement('p');
        domains.textContent = figure.domains || figure.summary;
        card.append(title, meta, domains);
        detailsConnections?.appendChild(card);
      });
      updateModeButtons();
      updateRouteButtons();
      updateCards();
      scheduleFit(animate ? 420 : 0, 90, 0, 48);
    };

    const showThematicRoute = (routeId, animate = true) => {
      const route = thematicRoutes[routeId];
      if (!route) return;
      stopMapMotion();
      setSurfaceMode('map');
      visibleRelationshipLimit = 4;
      activeMode = 'route';
      activeType = 'all';
      activeFigure = 'all';
      activeThematicRoute = routeId;
      activeGuidedRoute = null;
      const routeFigures = figures.filter((figure) => matchesEra(figure)
        && route.keywords.some((keyword) => normalizedText(figure.domains, figure.summary, figure.figureType).includes(keyword)));
      const routeFigureIds = new Set(routeFigures.map((figure) => figure.id));
      const routeRelationships = relationships.filter((relationship) => routeFigureIds.has(relationship.source)
        && routeFigureIds.has(relationship.target)
        && relationshipMatchesEra(relationship));
      activeRouteRelationshipIds = new Set(routeRelationships.map((relationship) => relationship.id));
      cy.$(':selected').unselect();
      cy.batch(() => {
        cy.elements().addClass('is-hidden').removeClass(`is-focused is-route is-constellation is-compact-label constellation-norse constellation-slavic ${relatedClassNames}`);
        routeFigures.forEach((figure) => cy.getElementById(figure.id).data('label', figureLabel(figure, true)).removeClass('is-hidden'));
        routeRelationships.forEach((relationship) => cy.getElementById(relationship.id).removeClass('is-hidden').addClass('is-focused'));
      });
      const visible = cy.elements().filter((element) => !element.hasClass('is-hidden'));
      visible.layout({
        name: routeFigures.length > 12 ? 'grid' : 'circle',
        animate: false,
        fit: false,
        padding: 80,
        spacingFactor: 1.2
      }).run();
      const routeLabel = themeButtons.find((button) => button.dataset.atlasTheme === routeId)?.textContent.trim() || routeId;
      setDetails({
        eyebrow: labels.routeTitle || 'Guided thematic route',
        title: routeLabel,
        meta: (labels.routeFigures || '[count] sacred figures').replace('[count]', routeFigures.length),
        text: routeFigures.slice(0, 12).map((figure) => `${figure.name} (${figure.traditionName})`).join(' · ')
      });
      updateModeButtons();
      updateRouteButtons();
      updateCards();
      scheduleFit(animate ? 420 : 0, 84, 0, 48);
    };

    const timelineEras = [
      { id: 'early', label: 'Bronze Age & early ancient', start: -3500, end: -1200 },
      { id: 'classical', label: 'Archaic–Hellenistic', start: -1200, end: 0 },
      { id: 'roman', label: 'Roman & Late Antiquity', start: 0, end: 600 },
      { id: 'medieval', label: 'Medieval sources', start: 600, end: 1500 },
      { id: 'modern', label: 'Modern Paganism', start: 1500, end: 2026 }
    ];
    const timelinePosition = (year) => {
      const eraIndex = timelineEras.findIndex((era, index) => year >= era.start
        && (year < era.end || index === timelineEras.length - 1));
      const index = eraIndex < 0 ? (year < timelineEras[0].start ? 0 : timelineEras.length - 1) : eraIndex;
      const era = timelineEras[index];
      const clamped = Math.max(era.start, Math.min(era.end, year));
      const withinEra = (clamped - era.start) / Math.max(1, era.end - era.start);
      return ((index + withinEra) / timelineEras.length) * 100;
    };
    const eventMatchesActiveEra = (event) => {
      if (activeEra === 'all') return true;
      const era = timelineEras.find((item) => item.id === activeEra);
      if (!era) return true;
      return event.endYear >= era.start && event.startYear <= era.end;
    };
    const humanize = (value) => String(value || 'Historical evidence')
      .replace(/[_-]+/g, ' ')
      .replace(/\b\w/g, (letter) => letter.toUpperCase());
    const showTimelineEventDetails = (event) => {
      const figure = figuresById.get(event.figureId);
      const links = event.sourceUrl ? [{
        text: event.sourceLabel || labels.viewSource || 'View source',
        url: event.sourceUrl,
        external: true
      }] : [];
      const connections = figure ? [{
        id: figure.id,
        label: `${labels.timelineViewFigure || 'View this figure on the map'}: ${figure.name}`
      }] : [];
      setDetails({
        eyebrow: labels.timelineEvent || 'Historical event',
        title: event.name,
        meta: [event.dateLabel, event.traditionName, humanize(event.eventType)].filter(Boolean).join(' · '),
        text: event.summary,
        links,
        connections,
        badges: event.evidence ? [{ text: event.evidence, tone: evidenceTone(event.evidence) }] : []
      });
    };
    const renderTimeline = () => {
      if (!timelineAxis || !timelineLanes || !timelineEmpty) return;
      clearElement(timelineAxis);
      clearElement(timelineLanes);
      timelineEras.forEach((era) => {
        const label = document.createElement('span');
        label.className = 'deity-atlas__timeline-era';
        label.textContent = era.label;
        timelineAxis.appendChild(label);
      });
      const visibleEvents = historicalEvents.filter((event) => (
        (timelineScope === 'all' || event.traditionId === activeTradition)
        && eventMatchesActiveEra(event)
      ));
      timelineEmpty.hidden = visibleEvents.length > 0;
      const grouped = new Map();
      visibleEvents.forEach((event) => {
        const groupId = timelineScope === 'all' ? event.traditionId : event.eventType;
        const groupLabel = timelineScope === 'all' ? event.traditionName : humanize(event.eventType);
        if (!grouped.has(groupId)) grouped.set(groupId, { label: groupLabel, events: [] });
        grouped.get(groupId).events.push(event);
      });
      Array.from(grouped.values()).forEach((group) => {
        const lane = document.createElement('div');
        lane.className = 'deity-atlas__timeline-lane';
        const laneLabel = document.createElement('strong');
        laneLabel.className = 'deity-atlas__timeline-lane-label';
        laneLabel.textContent = group.label;
        const track = document.createElement('div');
        track.className = 'deity-atlas__timeline-track';
        const rows = [];
        group.events.sort((a, b) => a.startYear - b.startYear).forEach((event) => {
          const x = timelinePosition(event.startYear);
          let rowIndex = rows.findIndex((previousX) => Math.abs(previousX - x) > 17);
          if (rowIndex < 0) {
            rowIndex = rows.length;
            rows.push(x);
          } else rows[rowIndex] = x;
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'deity-atlas__timeline-event';
          button.style.setProperty('--timeline-x', `${Math.max(7, Math.min(93, x))}%`);
          button.style.setProperty('--timeline-y', `${10 + rowIndex * 70}px`);
          const date = document.createElement('small');
          date.textContent = event.dateLabel;
          const title = document.createElement('strong');
          title.textContent = event.name;
          button.append(date, title);
          button.addEventListener('click', () => {
            showTimelineEventDetails(event);
            if (mobileSheetsAvailable()) showMobileTab('info', false);
          });
          track.appendChild(button);
        });
        const height = Math.max(90, rows.length * 70 + 12);
        track.style.minHeight = `${height}px`;
        lane.style.minHeight = `${height}px`;
        lane.append(laneLabel, track);
        timelineLanes.appendChild(lane);
      });
    };
    const showTimeline = () => {
      stopMapMotion();
      activeMode = 'timeline';
      activeFigure = 'all';
      activeRouteRelationshipIds = null;
      activeThematicRoute = null;
      activeGuidedRoute = null;
      setSurfaceMode('timeline');
      renderTimeline();
      if (timelineScroll) {
        timelineScroll.scrollTop = 0;
        timelineScroll.scrollLeft = 0;
      }
      const tradition = traditionsById.get(activeTradition);
      setDetails({
        eyebrow: labels.timelineTitle || 'Historical timeline',
        title: timelineScope === 'all' ? (labels.timelineAllCultures || 'All cultures') : tradition?.name,
        meta: `${historicalEvents.filter((event) => timelineScope === 'all' || event.traditionId === activeTradition).length} events`,
        text: labels.timelineIntro || ''
      });
      updateButtons();
      updateModeButtons();
      updateRouteButtons();
      updateCards();
    };

    const showGuidedRoute = (routeId, stepIndex = 0, animate = true) => {
      const route = guidedRoutesById.get(routeId);
      const steps = guidedStepsByRoute.get(routeId) || [];
      if (!route || !steps.length) return;
      stopMapMotion();
      setSurfaceMode('map');
      activeMode = 'guided';
      activeFigure = 'all';
      activeType = 'all';
      activeThematicRoute = null;
      activeGuidedRoute = routeId;
      activeGuidedStep = Math.max(0, Math.min(steps.length - 1, stepIndex));
      activeEra = 'all';
      if (eraSelect) eraSelect.value = 'all';
      activeRouteRelationshipIds = new Set();
      const routeFigureIds = new Set(steps.map((step) => step.figureId));
      const currentStep = steps[activeGuidedStep];
      const currentFigure = figuresById.get(currentStep.figureId);
      if (currentFigure) activeTradition = currentFigure.traditionId;
      cy.$(':selected').unselect();
      cy.batch(() => {
        cy.elements().addClass('is-hidden').removeClass(`is-focused is-route is-constellation is-compact-label constellation-norse constellation-slavic ${relatedClassNames}`);
        steps.forEach((step, index) => {
          const figure = figuresById.get(step.figureId);
          if (!figure) return;
          cy.getElementById(figure.id)
            .data('label', figureLabel(figure, true))
            .removeClass('is-hidden')
            .addClass('is-route')
            .toggleClass('is-focused', index === activeGuidedStep)
            .position({
              x: (index - (steps.length - 1) / 2) * (container.clientWidth < 720 ? 135 : 180),
              y: index % 2 === 0 ? -55 : 55
            });
        });
        steps.slice(0, -1).forEach((step, index) => {
          cy.getElementById(`guided-${routeId}-${index}`).removeClass('is-hidden');
        });
      });
      cy.elements().filter((element) => !element.hasClass('is-hidden')).layout({
        name: 'preset',
        animate: false,
        fit: false,
        padding: 70
      }).run();
      const sourceLinks = currentStep.sourceUrl ? [{
        text: currentStep.sourceLabel || labels.viewSource || 'View source',
        url: currentStep.sourceUrl,
        external: true
      }] : [];
      setDetails({
        eyebrow: labels.guidedRoute || 'Guided route',
        title: currentStep.heading || currentFigure?.name || route.name,
        meta: (labels.routeStep || 'Step [current] of [total]')
          .replace('[current]', activeGuidedStep + 1)
          .replace('[total]', steps.length),
        text: currentStep.narrative || route.summary,
        links: sourceLinks,
        badges: currentStep.transitionType ? [{ text: humanize(currentStep.transitionType) }] : []
      });
      const progress = document.createElement('div');
      progress.className = 'deity-atlas__route-progress';
      progress.textContent = `${route.name} · ${route.duration || ''}`;
      detailsConnections?.appendChild(progress);
      if (activeGuidedStep === 0 && route.note) {
        const routeNote = document.createElement('p');
        routeNote.className = 'deity-atlas__route-transition';
        routeNote.textContent = route.note;
        detailsConnections?.appendChild(routeNote);
      }
      if (currentStep.transitionLabel) {
        const transition = document.createElement('p');
        transition.className = 'deity-atlas__route-transition';
        transition.textContent = `Next connection: ${currentStep.transitionLabel}`;
        detailsConnections?.appendChild(transition);
      }
      const controls = document.createElement('div');
      controls.className = 'deity-atlas__route-controls';
      const previous = document.createElement('button');
      previous.type = 'button';
      previous.textContent = labels.routePrevious || 'Previous';
      previous.disabled = activeGuidedStep === 0;
      previous.addEventListener('click', () => showGuidedRoute(routeId, activeGuidedStep - 1));
      const next = document.createElement('button');
      next.type = 'button';
      next.textContent = activeGuidedStep === steps.length - 1
        ? (labels.routeComplete || 'Route complete')
        : (labels.routeNext || 'Next');
      next.disabled = activeGuidedStep === steps.length - 1;
      next.addEventListener('click', () => showGuidedRoute(routeId, activeGuidedStep + 1));
      const exit = document.createElement('button');
      exit.type = 'button';
      exit.textContent = labels.routeExit || 'Exit route';
      exit.addEventListener('click', () => showPantheon(activeTradition));
      controls.append(previous, next, exit);
      detailsConnections?.appendChild(controls);
      populateFigurePicker();
      updateButtons();
      updateModeButtons();
      updateRouteButtons();
      updateCards();
      scheduleFit(animate ? 420 : 0, 76, 0, 48);
    };

    const focusFigure = (figureId, animate = true) => {
      const figure = figuresById.get(figureId);
      if (!figure) return;
      stopMapMotion();
      setSurfaceMode('map');
      if (activeFigure === 'all') figureReturnMode = activeMode === 'family' ? 'family' : 'pantheon';
      visibleRelationshipLimit = 4;
      activeMode = 'pantheon';
      activeThematicRoute = null;
      activeGuidedRoute = null;
      if (figure.traditionId !== activeTradition) activeTradition = figure.traditionId;
      activeFigure = figureId;
      const connectedRelationships = matchingRelationships(figureId);
      const comparativeConstellation = activeType === 'all' ? findComparativeConstellation(figureId) : null;
      const culturalRoute = activeType === 'all' && !comparativeConstellation ? findCulturalRoute(figureId) : null;
      const connectedIds = new Set([figureId]);
      const neighbourGroups = new Map();
      connectedRelationships.forEach((relationship) => {
        connectedIds.add(relationship.source);
        connectedIds.add(relationship.target);
        const relatedId = relationship.source === figureId ? relationship.target : relationship.source;
        const currentGroup = neighbourGroups.get(relatedId);
        const nextPriority = relationshipGroups.findIndex((group) => group.id === relationship.typeGroup);
        const currentPriority = relationshipGroups.findIndex((group) => group.id === currentGroup);
        if (!currentGroup || nextPriority < currentPriority) neighbourGroups.set(relatedId, relationship.typeGroup);
      });

      const displayedRelationships = culturalRoute
        ? culturalRoute.relationships
        : (comparativeConstellation ? comparativeConstellation.relationships : connectedRelationships);
      const displayedIds = culturalRoute
        ? new Set(culturalRoute.nodes)
        : (comparativeConstellation ? new Set(comparativeConstellation.figures) : connectedIds);
      activeRouteRelationshipIds = culturalRoute || comparativeConstellation
        ? new Set(displayedRelationships.map((relationship) => relationship.id))
        : null;
      const compactFocus = container.clientWidth < 520;

      cy.batch(() => {
        cy.elements().addClass('is-hidden').removeClass(`is-focused is-route is-constellation is-compact-label constellation-norse constellation-slavic ${relatedClassNames}`);
        displayedIds.forEach((id) => cy.getElementById(id).removeClass('is-hidden'));
        displayedRelationships.forEach((relationship) => {
          const edge = cy.getElementById(relationship.id).removeClass('is-hidden').addClass(
            culturalRoute ? 'is-route' : (comparativeConstellation ? 'is-constellation' : 'is-focused')
          );
          if (compactFocus) edge.addClass('is-compact-label');
        });
        cy.getElementById(figureId).removeClass('is-hidden').addClass('is-focused').select();
      });

      const center = cy.getElementById(figureId);
      if (culturalRoute) {
        const midpoint = (culturalRoute.nodes.length - 1) / 2;
        culturalRoute.nodes.forEach((id, index) => {
          const routeFigure = figuresById.get(id);
          const adjacentRelationship = culturalRoute.relationships[index] || culturalRoute.relationships[index - 1];
          cy.getElementById(id)
            .data('label', figureLabel(routeFigure, true))
            .addClass(`is-route is-related-${adjacentRelationship.typeGroup}`)
            .position(compactFocus
              ? { x: 0, y: (index - midpoint) * 160 }
              : { x: (index - midpoint) * 220, y: 0 });
        });
      } else if (comparativeConstellation) {
        const positions = compactFocus ? comparativeConstellation.compactPositions : comparativeConstellation.desktopPositions;
        comparativeConstellation.figures.forEach((id) => {
          const constellationFigure = figuresById.get(id);
          cy.getElementById(id)
            .data('label', figureLabel(constellationFigure, true))
            .addClass(`is-constellation constellation-${constellationFigure.traditionId}`)
            .position(positions[id]);
        });
      } else {
        displayedIds.forEach((id) => cy.getElementById(id).data('label', figureLabel(figuresById.get(id))));
        center.position({ x: 0, y: 0 });
      }
      const activeGroups = relationshipGroups.map((group) => ({
        ...group,
        members: Array.from(neighbourGroups.entries()).filter(([, groupId]) => groupId === group.id).map(([id]) => id)
      })).filter((group) => !culturalRoute && !comparativeConstellation && group.members.length > 0);

      activeGroups.forEach((group, groupIndex) => {
        const radius = (compactFocus ? 125 : 170) + groupIndex * (compactFocus ? 42 : 65);
        const ring = cy.getElementById(`focus-ring-${group.id}`);
        ring
          .data('diameter', radius * 2)
          .data('label', '')
          .position({ x: 0, y: 0 })
          .removeClass('is-hidden');
        group.members.forEach((id, index) => {
          const phase = -Math.PI / 2 + groupIndex * 0.82;
          const angle = phase + (Math.PI * 2 * index) / Math.max(1, group.members.length);
          cy.getElementById(id)
            .addClass(`is-related-${group.id}`)
            .position({ x: Math.cos(angle) * radius, y: Math.sin(angle) * radius });
        });
      });

      populateFigurePicker();
      if (figureSelect) figureSelect.value = figureId;
      showFigureDetails(figure);
      updateButtons();
      updateModeButtons();
      updateRouteButtons();
      updateCards();
      scheduleFit(
        animate ? 420 : 0,
        compactFocus ? 26 : 82,
        compactFocus ? 28 : 0,
        compactFocus ? 0 : 68
      );
    };

    const updateCompareButton = () => {
      if (compareButton) compareButton.disabled = !compareA?.value || !compareB?.value || compareA.value === compareB.value;
    };

    const returnFromFigure = () => {
      activeType = figureReturnMode === 'family' ? 'kinship' : 'all';
      activeThematicRoute = null;
      if (figureReturnMode === 'family') showFamilyTree(activeTradition);
      else {
        activeMode = 'pantheon';
        showPantheon(activeTradition);
      }
    };

    typeButtons.forEach((button) => button.addEventListener('click', () => {
      activeType = button.dataset.atlasType;
      activeMode = 'pantheon';
      activeThematicRoute = null;
      if (activeFigure === 'all') showPantheon(activeTradition, false);
      else focusFigure(activeFigure, false);
      if (mobileSheetsAvailable()) showMobileTab('info', false);
    }));
    traditionButtons.forEach((button) => button.addEventListener('click', () => {
      activeTradition = button.dataset.atlasTradition;
      if (activeMode === 'timeline') showTimeline();
      else if (activeMode === 'family') showFamilyTree(activeTradition);
      else {
        activeMode = 'pantheon';
        showPantheon(activeTradition);
      }
      if (mobileSheetsAvailable()) showMobileTab('info', false);
    }));
    modeButtons.forEach((button) => button.addEventListener('click', () => {
      const mode = button.dataset.atlasMode;
      activeThematicRoute = null;
      if (mode === 'family') showFamilyTree(activeTradition);
      else if (mode === 'timeline') showTimeline();
      else {
        activeMode = 'pantheon';
        activeType = 'all';
        showPantheon(activeTradition);
      }
      if (mobileSheetsAvailable()) showMobileTab('info', false);
    }));
    figureSelect?.addEventListener('change', () => {
      activeType = 'all';
      activeThematicRoute = null;
      if (figureSelect.value === 'all') {
        if (activeFigure !== 'all') returnFromFigure();
        else if (activeMode === 'family') showFamilyTree(activeTradition);
        else showPantheon(activeTradition);
      }
      else focusFigure(figureSelect.value);
    });
    backButton?.addEventListener('click', () => {
      returnFromFigure();
    });
    searchInput?.addEventListener('input', () => renderSearchResults(searchInput.value));
    searchInput?.addEventListener('focus', () => {
      if (searchInput.value.trim()) renderSearchResults(searchInput.value);
    });
    searchInput?.addEventListener('blur', () => window.setTimeout(() => {
      if (searchResults) searchResults.hidden = true;
      searchInput.setAttribute('aria-expanded', 'false');
    }, 180));
    eraSelect?.addEventListener('change', () => {
      activeEra = eraSelect.value;
      if (activeMode === 'timeline') showTimeline();
      else if (activeMode === 'family') showFamilyTree(activeTradition, false);
      else if (activeMode === 'route' && activeThematicRoute) showThematicRoute(activeThematicRoute, false);
      else {
        activeMode = 'pantheon';
        showPantheon(activeTradition, false);
      }
      if (mobileSheetsAvailable()) showMobileTab('info', false);
    });
    compareA?.addEventListener('change', updateCompareButton);
    compareB?.addEventListener('change', updateCompareButton);
    compareButton?.addEventListener('click', () => {
      showComparison(compareA.value, compareB.value);
      if (mobileSheetsAvailable()) showMobileTab('info', true);
    });
    themeButtons.forEach((button) => button.addEventListener('click', () => {
      const routeId = button.dataset.atlasTheme;
      if (activeMode === 'route' && activeThematicRoute === routeId) {
        activeMode = 'pantheon';
        activeThematicRoute = null;
        showPantheon(activeTradition);
      } else showThematicRoute(routeId);
      if (mobileSheetsAvailable()) showMobileTab('info', false);
    }));
    guidedRouteButtons.forEach((button) => button.addEventListener('click', () => {
      showGuidedRoute(button.dataset.atlasGuidedRoute, 0);
      if (mobileSheetsAvailable()) showMobileTab('info', true);
    }));
    timelineScopeButtons.forEach((button) => button.addEventListener('click', () => {
      timelineScope = button.dataset.atlasTimelineScope;
      timelineScopeButtons.forEach((item) => {
        const selected = item.dataset.atlasTimelineScope === timelineScope;
        item.classList.toggle('is-active', selected);
        item.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });
      showTimeline();
    }));
    zoomIn?.addEventListener('click', () => cy.zoom({ level: Math.min(cy.maxZoom(), cy.zoom() * 1.2), renderedPosition: { x: container.clientWidth / 2, y: container.clientHeight / 2 } }));
    zoomOut?.addEventListener('click', () => cy.zoom({ level: Math.max(cy.minZoom(), cy.zoom() / 1.2), renderedPosition: { x: container.clientWidth / 2, y: container.clientHeight / 2 } }));
    fitButton?.addEventListener('click', () => scheduleFit());
    resetButton?.addEventListener('click', () => {
      activeType = 'all';
      activeEra = 'all';
      activeMode = 'pantheon';
      activeThematicRoute = null;
      if (eraSelect) eraSelect.value = 'all';
      if (searchInput) searchInput.value = '';
      showPantheon(activeTradition);
    });
    showMoreButton?.addEventListener('click', () => {
      visibleRelationshipLimit += 2;
      updateCards();
    });

    mobileOpenButton?.addEventListener('click', openMobileFullscreen);
    mobileCloseButton?.addEventListener('click', closeMobileFullscreen);
    bindMobileSheetDrag(details, mobileSheetToggle, 'info');
    mobilePanelHandles.forEach((handle) => {
      const panelId = handle.dataset.atlasMobilePanelHandle;
      const panel = panelId === 'pantheons' ? mobileFiltersPanel : mobileExplorerPanel;
      bindMobileSheetDrag(panel, handle, panelId);
    });
    mobileTabs.forEach((button) => button.addEventListener('click', () => {
      const tabId = button.dataset.atlasMobileTab;
      const isCurrent = button.classList.contains('is-active');
      if (isCurrent && tabId !== 'info') {
        showMobileTab('none');
      } else if (isCurrent && tabId === 'info' && details?.classList.contains('is-mobile-sheet-dismissed')) {
        showMobileTab('info', false);
      } else {
        showMobileTab(tabId, isCurrent && tabId === 'info' && !details?.classList.contains('is-mobile-sheet-expanded'));
      }
    }));
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !isMobileFullscreen) return;
      if (details?.classList.contains('is-mobile-sheet-expanded') || atlas.classList.contains('has-mobile-panel')) {
        showMobileTab('info', false);
      } else if (activeFigure !== 'all') {
        returnFromFigure();
      } else closeMobileFullscreen();
    });
    window.addEventListener('resize', () => {
      window.requestAnimationFrame(() => {
        mountMobileViewportLayers();
        cy.resize();
        if (isMobileFullscreen) {
          scheduleFit(
            0,
            mobileMedia.matches ? 28 : 65,
            mobileMedia.matches ? 10 : 0
          );
      } else if (mobileMedia.matches) {
        scheduleFit(0, 38, 0);
      }
      });
    });

    cy.on('tap', 'node.figure', (event) => {
      if (activeMode === 'guided' && activeGuidedRoute) {
        const routeSteps = guidedStepsByRoute.get(activeGuidedRoute) || [];
        const stepIndex = routeSteps.findIndex((step) => step.figureId === event.target.id());
        if (stepIndex >= 0) showGuidedRoute(activeGuidedRoute, stepIndex);
        else focusFigure(event.target.id());
      } else focusFigure(event.target.id());
      if (mobileSheetsAvailable()) showMobileTab('info', false);
    });
    cy.on('tap', 'edge.relationship', (event) => {
      const relationship = relationshipsById.get(event.target.id());
      if (relationship) {
        showRelationshipDetails(relationship);
        if (mobileSheetsAvailable()) showMobileTab('info', false);
      }
    });
    cy.on('tap', (event) => {
      if (event.target === cy && activeFigure !== 'all') {
        returnFromFigure();
        if (mobileSheetsAvailable()) showMobileTab('info', false);
      }
    });

    new ResizeObserver(() => cy.resize()).observe(container);
    updateCompareButton();
    if (activeTradition) showPantheon(activeTradition, false);
    if (mobileMedia.matches) showMobileTab('info', false);
  };

  const initAll = (root = document) => root.querySelectorAll('[data-deity-atlas]').forEach(initAtlas);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
